/**
 * Arrange: give a score, say who plays, and have it arranged.
 *
 * Three questions and a button, down the left: the score (`ArrangeStepScore`), its players
 * (`ArrangeStepPlayers`), and anything else, in words (`ArrangeStepWish`). Everything else
 * arranging can be told is chosen for the players named and the score (`lib/arrange-auto`), and each of
 * those choices is a control of its own in the lab (`LabPage`), which this screen never sends
 * its reader to.
 *
 * Pressing Arrange with no words arranges the whole piece as chosen. With words they are read
 * first, against what was chosen (`lib/arrange-wish`), and what they ask for is arranged:
 * other players, a window of bars, more or less added. A wish that names its players is
 * enough without the second question, which then shows who it named. Each press begins from
 * what is chosen for the players then named and nothing from the press before, and words
 * pressed again are read against the players they were first read against, so the same score,
 * players and words arrange the same. Words that change nothing are said to have.
 *
 * The right is the score as it was written and then, beside it under a switch, the
 * arrangement (`ArrangeDesks`), each with its own play, downloads and way into the editor.
 * Over them one line says what the arrangement is (`ArrangeSummary`). A score can arrive
 * named by its id in the address (`/arrange?score=<id>`), which is how the Library hands one
 * over: it is fetched and drawn as an upload is, and arranged by its id. Read hands the
 * reading of a scan over with the navigation (`handedPick`), and it is the score given, as
 * the same scan opened here would be.
 *
 * Wide, the two halves are a window, each scrolling in itself. Narrow, the questions come
 * first and the score under them, and the page scrolls as one.
 */

import { useMemo, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import type { AskPayload, LibraryEntry } from "@app/shared-types";
import { api } from "../api/client";
import { useAsync } from "../api/useAsync";
import ArrangeAction from "../components/ArrangeAction";
import ArrangeDesks from "../components/ArrangeDesks";
import { saying, sayingPhotos } from "../components/ArrangeProgress";
import ArrangeStepPlayers from "../components/ArrangeStepPlayers";
import ArrangeStepScore from "../components/ArrangeStepScore";
import ArrangeStepWish from "../components/ArrangeStepWish";
import ArrangeSummary from "../components/ArrangeSummary";
import LibraryCredit from "../components/LibraryCredit";
import { boardFor, chosenAgain, shapeOf, unchanged } from "../lib/arrange-auto";
import { useArrangeJob } from "../lib/arrange-job";
import { useOpenScore } from "../lib/arrange-open";
import { handedPick, type Picked } from "../lib/arrange-pick";
import { useWish } from "../lib/arrange-wish";
import { askedWindow, type Board, boardOf, heldBack, type Ran, requestOf } from "../lib/ask-run";
import { fileName, SCORE_PARAM, useLibraryScore } from "../lib/library";
import { sameSeats } from "../lib/roster";
import { barsOf, DRAWN_BARS, original, parsed } from "../lib/score-window";

/** What Arrange is waiting for before it can be pressed, or `null` where it can be. */
function waitingFor(scored: boolean, named: boolean, asked: boolean): string | null {
  if (asked) return "Answer the question above and the arranging starts.";
  if (!scored) return named ? "Waiting for a score." : "Waiting for a score and its players.";
  return named ? null : "Say who plays: pick them above, or name them in words.";
}

/** Words that were read and asked for nothing the arranger can be told. */
const NOTHING_ASKED =
  "Nothing in your words changes how this is arranged, so it is arranged as it would be without them.";

/** Words that were read: the players they were read against, and the players they named. */
interface Named {
  readonly words: string;
  readonly before: readonly string[];
  readonly after: readonly string[];
}

export default function ArrangePage(): JSX.Element {
  const navigate = useNavigate();
  const seats = useAsync("seats", () => api.seats());
  const known = seats.data?.seats ?? [];

  const handed = useLocation().state;
  const [picked, setPicked] = useState<Picked | null>(() => handedPick(handed));
  // Nobody is seated for the reader: who plays is the one thing only they can say.
  const [roster, setRoster] = useState<string[]>([]);
  const [words, setWords] = useState("");
  const [named, setNamed] = useState<Named | null>(null);
  // The run on the page, kept for the players it was for and for another version of it.
  const [ran, setRan] = useState<Ran | null>(null);
  const job = useArrangeJob();
  const { result } = job;

  const [params, setParams] = useSearchParams();
  const wanted = params.get(SCORE_PARAM);
  const [credit, setCredit] = useState<LibraryEntry | null>(null);

  const launch = (next: Ran): void => {
    if (picked === null) return;
    setRan(next);
    job.run(requestOf(next, picked, credit?.id ?? null));
  };
  // An answer names the players it arranges for, so the second question shows them.
  const wish = useWish((answer: AskPayload, sent: Board): string | null => {
    const next = chosenAgain(sent, boardOf(sent, answer), shape);
    setRoster([...next.roster]);
    setNamed({ words, before: sent.roster, after: next.roster });
    const held = heldBack(next, picked !== null);
    if (held !== null) return held;
    launch({ board: next, bars: askedWindow(next), take: 1, preview: false });
    return answer.unmet === null && unchanged(sent, next, total) ? NOTHING_ASKED : null;
  });
  // The arrangement, and what was said of the wish, belong to the score they were made from.
  const opened = (next: Picked | null, entry: LibraryEntry | null): void => {
    setPicked(next);
    setCredit(entry);
    job.clear();
    wish.clear();
  };
  const library = useLibraryScore(wanted, ({ entry, text }) =>
    opened({ name: fileName(entry), text, note: null }, entry),
  );
  const opening = useOpenScore((next) => {
    opened(next, null);
    if (wanted !== null) setParams({}, { replace: true });
  });

  const musicxml = result?.musicxml ?? null;
  const read = useMemo(() => parsed(musicxml), [musicxml]);
  const arranged = useMemo(() => read.score && barsOf(read.score, 1, DRAWN_BARS).score, [read]);
  // Read once per score. Before a run the whole of it is drawn, and after one the bars the
  // arranger read, so that both desks hold the same music.
  const whole = useMemo(() => parsed(picked?.text ?? null), [picked]);
  const from = result?.source.first ?? 1;
  const to = result?.source.last ?? 100000;
  const source = useMemo(
    () => original(picked?.name ?? null, whole, from, to),
    [picked, whole, from, to],
  );
  const score = whole.score;
  // Who plays is chosen for against the score's staves and its chord symbols.
  const shape = useMemo(() => shapeOf(score), [score]);
  // The same words over the players they named are read against the players they were first
  // read against, so "add a flute" pressed twice adds one flute and not two.
  const again = named !== null && named.words === words && sameSeats(named.after, roster);
  const base = boardFor(again ? named.before : roster, shape);
  const total = score?.measures.length ?? null;
  const facts = score && { bars: score.measures.length, parts: score.parts.map((p) => p.name) };

  const wished = words.trim() !== "";
  const arrange = (): void => {
    if (wished) {
      wish.ask(words, base, facts, total, null);
      return;
    }
    wish.clear();
    launch({ board: boardFor(roster, shape), bars: undefined, take: 1, preview: false });
  };
  const busy =
    opening.progress !== null
      ? opening.photos === null
        ? saying(opening.progress)
        : sayingPhotos(opening.progress)
      : wish.reading
        ? "Reading what you asked for…"
        : job.progress && saying(job.progress);
  const stop = opening.progress !== null ? opening.cancel : wish.reading ? wish.stop : job.cancel;

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-y-auto lg:flex-row lg:overflow-hidden">
      <section
        aria-label="What to arrange"
        className="flex shrink-0 flex-col gap-6 border-b border-shell-edge bg-shell-bar px-6 pb-6 pt-7 lg:w-[400px] lg:overflow-y-auto lg:border-b-0 lg:border-r"
      >
        <div className="flex flex-col gap-1.5">
          <h1 className="font-display text-[26px] font-medium leading-tight text-shell-lit">
            Arrange a score
          </h1>
          <p className="text-[14px] text-shell-text">
            Give the score and say who plays. The rest is ours.
          </p>
        </div>
        <ArrangeStepScore
          name={opening.photos?.name ?? picked?.name ?? null}
          facts={facts}
          photos={opening.photos?.count ?? null}
          fetching={library.loading ? wanted : null}
          trouble={
            // Photos being read are a score of their own: nothing said of the one before them.
            opening.photos !== null
              ? null
              : library.error === null
                ? (picked?.note ?? null)
                : `The Library could not hand over ${wanted}: ${library.error}`
          }
          onOpen={opening.open}
        />
        <ArrangeStepPlayers
          roster={roster}
          seats={known}
          trouble={seats.error}
          onRoster={(next) => {
            // Words being read are read against the players as they were; an answer would
            // put back the players changed since, so the reading is dropped.
            if (wish.reading) wish.stop();
            setRoster(next);
          }}
        />
        <ArrangeStepWish
          words={words}
          question={wish.question}
          said={wish.said}
          reading={wish.reading}
          onWords={(next) => {
            // A question, and what was said of a wish, are about the words they were asked of.
            if (wish.question !== null || wish.said !== null) wish.clear();
            setWords(next);
          }}
          onChoose={(choice) => {
            if (wish.question === null) return;
            const answered = { question: wish.question.text, choice };
            wish.ask(words, base, facts, total, answered);
          }}
        />
        <ArrangeAction
          busy={busy}
          waiting={waitingFor(picked !== null, roster.length > 0 || wished, wish.question !== null)}
          meanwhile={
            opening.photos === null
              ? null
              : "The score is being read off the photos; say who plays meanwhile."
          }
          arranged={result !== null}
          another={result !== null && ran !== null && result.start === "model"}
          trouble={job.error}
          onArrange={arrange}
          onAnother={() => ran && launch({ ...ran, take: ran.take + 1 })}
          onStop={stop}
        />
      </section>

      <section
        aria-label="The score"
        className="flex h-[85dvh] shrink-0 flex-col bg-canvas lg:h-auto lg:min-h-0 lg:min-w-0 lg:flex-1 lg:shrink"
      >
        {picked === null && !library.loading ? (
          <p className="m-auto max-w-md p-8 text-center font-display text-[20px] text-shell-lit">
            The score, and then its arrangement, are laid out here.
          </p>
        ) : (
          <>
            {credit === null ? null : <LibraryCredit entry={credit} />}
            {result === null || ran === null ? null : (
              <ArrangeSummary result={result} roster={ran.board.roster} seats={known} />
            )}
            <ArrangeDesks
              wanted={wanted}
              fetching={library.loading}
              source={source}
              waiting="Press Arrange, and the arrangement is drawn here, to be played against the original."
              beside={false}
              result={result}
              read={read}
              arranged={arranged}
              onOpenInEditor={(text) => navigate("/editor", { state: { musicxml: text } })}
            />
          </>
        )}
      </section>
    </div>
  );
}
